import { Component, OnInit } from '@angular/core';
import {UserPrivilegeService} from '../../../services/common/user-privilege/user-privilege.service';
import {
  NumberPortabilityApiService
} from '../../../services/api/settings/call-flow-configurations/number-portability/number-portability-api.service';
import {LicenseFeaturesService} from '../../../services/common/license-features/license-features.service';
import {LoaderService} from '../../../services/common/loader-service/loader.service';
import {MatDialog} from '@angular/material/dialog';
import {InformationDialogComponent} from '../../../dialogs/information-dialog/information-dialog.component';
import _ from 'lodash';
import {ConfirmationDialogComponent} from '../../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {DisplayedColumnInf} from '../../../interfaces/common/table/displayed-column-inf';
import {AcDialogService} from 'ac-infra';
import {WebServiceDialogComponent} from './web-service-dialog/web-service-dialog.component';
import {SnackBarService} from '../../../services/common/snack-bar/snack-bar.service';

@Component({
  selector: 'web-services',
  templateUrl: './web-services.component.html',
  styleUrls: ['./web-services.component.scss']
})
export class WebServicesComponent implements OnInit {

  implementations: any[] = [];
  numberPortabilityAgentTypes = [];

  canSubmit: boolean;
  templates: any;
  templateToValue: any;
  isFeatureSupported = false;

  displayedColumns: Array<DisplayedColumnInf> = [
    {name: 'name', display: 'name', selected: true,  width: 50},
    {name: 'numberPortabilityAgentType', display: 'Agent type', selected: true, width: 100}
  ];

  selection;

  constructor(private userPrivilege: UserPrivilegeService,
              private numberPortabilityApiService: NumberPortabilityApiService,
              private licenseFeaturesService: LicenseFeaturesService,
              private loaderService: LoaderService,
              private dialog: MatDialog,
              private acDialogService: AcDialogService,
              private snackBarService: SnackBarService){}

  ngOnInit(): void {
    this.canSubmit = this.userPrivilege.isAuthorized();
    this.refresh();

    this.licenseFeaturesService.isFeatureSupported('numberPortability').toPromise().then(isFeatureSupported => {
      this.isFeatureSupported = isFeatureSupported;

      if (!isFeatureSupported) {
        const dialogRef = this.dialog.open(InformationDialogComponent, {
          width: '460px',
          data: {
            message: 'Web Services are not supported by license'
          }
        });

        dialogRef.componentInstance.onApply.subscribe(() => {
          dialogRef.close();
        });
      }
    });
  }

  refresh(): void {
    this.getTemplates();
  }

  getTemplates(): void {
    this.numberPortabilityApiService.getNumberPortabilityTemplates().subscribe(templates => {
      this.templates = templates.map(this.templateItem);
      this.numberPortabilityAgentTypes = templates.map((template) => {
        return {
          text: template.numberPortabilityAgentType,
          value: template.numberPortabilityAgentType
        };
      });

      this.mapTemplateToAgent(templates);
      this.getWebServices();
    });
  }

  getWebServices(): void {
    this.loaderService.showMainContainer();

    this.numberPortabilityApiService.getNumberPortabilityImplementations().subscribe(implementations => {
      this.implementations = this.prepareData(implementations);
      this.updateNumberPortabilityAgentTypes();
    }, () => {

    }, () => {
      this.loaderService.hideMainContainer();
    });
  }

  prepareData(implementations): any {
    implementations.forEach(i => {
      i.params.forEach(param => {
        const paramData = this.templateToValue[i.numberPortabilityAgentType][param.name];
        if (_.isNil(paramData)) { return; }

        param.templateExist = true;
        param.isRequired = paramData.isRequired;
        param.minValue = paramData.minValue;
        param.maxValue = paramData.maxValue;
        param.options = paramData.options;
        param.displayCondition = paramData.displayCondition;
      });
    });
    return implementations;
  }

  mapTemplateToAgent(templates): void {
    this.templateToValue = {};
    templates.forEach((template) => {
      const innerMap = {};
      template.params.forEach(param => {
        if (param.type === 'booleanType') {
          param.value = (param.value === 'true');
        }
        innerMap[param.name] = param;
      });
      this.templateToValue[template.numberPortabilityAgentType] = innerMap;
    });
  }

  removeWebService(): void {
    if (this.selection) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message: `Are you sure you want to delete the web service: "${this.selection.name}" ?`
        }
      });

      dialogRef.componentInstance.onApply.subscribe(() => {
        this.numberPortabilityApiService.deleteNumberPortabilityImplementation(this.selection.id).subscribe(() => {
          this.updateNumberPortabilityAgentTypes();
          dialogRef.close();
          this.refresh();
          this.snackBarService.display(`${this.selection.name} successfully deleted`);
        }, () => {
        });
      });
    }
  }

  addWebService(): void {
    const dialogData = {
      data: _.cloneDeep(this.templates[0]),
      templates: this.templates,
      numberPortabilityAgentTypes: this.numberPortabilityAgentTypes,
      isEditMode: false
    };

    this.acDialogService.open(WebServiceDialogComponent, {
      dialogData,
      title: 'Add Web Service',
      onSubmit: () => {
        return this.numberPortabilityApiService.addNumberPortabilityImplementation(dialogData.data).toPromise().then(() => {
          this.refresh();
          this.snackBarService.display(`${dialogData.data.name} successfully added`);
        });
      }
    });
  }

  isAddDisabled(): boolean {
    return this.implementations.length !== 0 &&
      (this.implementations[this.implementations.length - 1].id === undefined ||
        this.implementations[this.implementations.length - 1].id === null);
  }

  private templateItem(template): any {
    template.name = !_.isNil(template.name) ? template.name : '';
    template.params.forEach(param => param.templateExist = true);
    return template;
  }

  updateNumberPortabilityAgentTypes(): any {
    const didMasking = this.implementations.find(item => !_.isNil(item.id) && item.numberPortabilityAgentType === 'did_masking');

    if (_.isNil(didMasking)) {
      this.numberPortabilityAgentTypes.forEach(item => item.disabled = false);
      return;
    }

    this.numberPortabilityAgentTypes.forEach(item => {
      if (item.value === 'did_masking') {
        item.disabled = true;
      }
    });
  }

  onSelection(selected: any): void {
    this.selection = selected;
  }

  editWebService(): void {
    const dialogData = {
      data: _.cloneDeep(this.selection),
      templates: this.templates,
      numberPortabilityAgentTypes: this.numberPortabilityAgentTypes,
      isEditMode: true
    };

    this.acDialogService.open(WebServiceDialogComponent, {
      dialogData,
      title: 'Edit Web Service',
      onSubmit: () => {
        this.numberPortabilityApiService.editNumberPortabilityImplementation(dialogData.data.id, dialogData.data).toPromise().then(() =>
        {
          this.refresh();
        });
      }
    });
  }
}
